<template>
  <div class="complete-group">
    <!-- loading, holds position while loading -->
    <div v-if="!isReady" class="complete-button">
      <complete-button
        :is-complete="false"
        :entity-type="entityType"
        :entity-category="entityCategory"
        incomplete-text="Loading..."
        complete-text=""
        :disabled="isFutureDate"
      />
    </div>

    <!-- not logged in, cannot complete -->
    <div v-if="isReady && !user.isLoggedIn" class="complete-button">
      <complete-button
        :is-complete="false"
        :entity-type="entityType"
        :entity-category="entityCategory"
        :disabled="isFutureDate"
        complete-text=""
        @click-action="handleNoAuthComplete"
      />
    </div>

    <!-- multiple scheduled, custom complete button for this component -->
    <multi-complete-button
      v-if="isReady && user.isLoggedIn && scheduleMode === 'multiple'"
      :entity-id="entityId"
      :entity-type="entityType"
      :entity-category="entityCategory"
      :tracker-defaults="trackerDefaults"
      :disabled="isFutureDate"
    />

    <!-- single scheduled, standard complete flow for single instance -->
    <scheduled-complete-button
      v-if="isReady && user.isLoggedIn && scheduleMode === 'single' && scheduledItems[0]"
      :scheduled-id="scheduledItems[0].id"
      :entity-id="entityId"
      :entity-type="entityType"
      :entity-category="entityCategory"
      :is-complete="!!scheduledItems[0].isComplete"
      :tracker-defaults="trackerDefaults"
      :disabled="isFutureDate"
    />

    <!-- not scheduled, adds to calendar and completes -->
    <unscheduled-complete-button
      v-if="isReady && user.isLoggedIn && scheduleMode === 'unscheduled'"
      :entity-id="entityId"
      :entity-type="entityType"
      :entity-category="entityCategory"
      :schedule-date="scheduleDate"
      :tracker-defaults="trackerDefaults"
      :disabled="isFutureDate"
    />
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import dayjs from 'dayjs'
import { mapState, mapActions } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { useModalsStore } from '@/stores/modals'
import { useCalendarStore } from '@/stores/calendar'
import { fbApi, objToSearchParams } from '@/utils/http-api'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import CompleteButton from '@/components/buttons/completes/complete-button.vue'
import ScheduledCompleteButton from '@/components/buttons/completes/scheduled-complete-button.vue'
import UnscheduledCompleteButton from '@/components/buttons/completes/unscheduled-complete-button.vue'
import MultiCompleteButton from '@/components/buttons/completes/multi-complete-button.vue'

dayjs.extend(customParseFormat)

/**
 * Complete button primarily used on details, outside of "day" context like the calendar.
 *
 * Use Cases 1 - Multiple scheduled
 * - on complete show flyout for scheduled instances to complete
 * - clicking on a schedule instance triggers complete flow for that instance
 * - on uncomplete, keep on calendar as incomplete
 *
 * User Case 2 - Single Scheduled
 * - no flyout
 * - on complete, go through complete flow
 * - on uncomplete, keep on calendar as incomplete
 *
 * Use Case 3 - Not Scheduled
 * - on complete, add to calendar for "scheduleDate" and then complete
 * - on uncomplete, remove from calendar for "scheduleDate"
 *
 * Use Case 4 - Not Logged In
 * If not logged in, show button and spawn login pop-up
 */
export default defineComponent({
  name: 'DetailCompleteButton',
  components: {
    MultiCompleteButton,
    UnscheduledCompleteButton,
    CompleteButton,
    ScheduledCompleteButton,
  },
  props: {
    entityId: {
      type: Number,
      required: true,
    },
    entityType: {
      default: 'workout',
      type: String,
      validator: (val) =>
        ['plan', 'workout', 'custom_workout', 'article', 'wellness_video'].includes(val),
    },

    // only used for plan / article complete
    // will be empty string for video / custom workouts
    entityCategory: {
      default: '',
      validator: (val) => ['', 'program', 'challenge', 'routine', 'recipe'].includes(val),
    },

    // YYYY-MM-DD in users TZ
    scheduleDate: {
      type: String,
      required: true,
    },

    // workouts { minutes, difficulty }
    // other trackers in the future
    trackerDefaults: {
      type: Object,
      default: () => ({}),
    },

    // detail view has separate desktop and mobile workout complete
    // buttons, only fire XHR on one of them as store is in pinia
    fetchDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      error: '',
    }
  },
  computed: {
    ...mapState(useUserAuthStore, ['features', 'user', 'isFbAdminUser']),
    ...mapState(useCalendarStore, ['scheduledItems']),

    // used for workout complete button on complete
    displayType() {
      // articles
      if (['article'].includes(this.entityType)) {
        // recipes
        if (['recipe'].includes(this.entityCategory)) {
          return 'Recipe'
        }

        return 'Article'
      }

      if (this.entityType === 'wellness_video') {
        return 'Video'
      }

      // workouts / programs / all others
      return 'Workout'
    },

    isReady() {
      return !this.error && !this.loading && Array.isArray(this.scheduledItems)
    },

    scheduleMode() {
      if (this.scheduledItems.length === 1) {
        return 'single'
      }

      if (this.scheduledItems.length > 1) {
        return 'multiple'
      }

      return 'unscheduled'
    },

    // cannot complete workouts in the future of "today" to mirror
    // calendar day and dashboard rules
    isFutureDate() {
      // today in user tz w/o time
      const todayNoTime = dayjs().format('YYYY-MM-DD')
      const userToday = dayjs(todayNoTime, 'YYYY-MM-DD')

      const scheduleDate = dayjs(this.scheduleDate, 'YYYY-MM-DD')

      return scheduleDate.isAfter(userToday)
    },
  },

  watch: {
    // two complete buttons can be on detail view, so mirror loading
    // state between them w/ only one XHR
    scheduledItems: function () {
      if (this.fetchDisabled && this.loading) {
        this.loading = false
      }
    },
  },

  // lifecycle methods
  created() {
    // no XHR if user not logged in or fb admin
    if (!this.user.isLoggedIn || this.isFbAdminUser) {
      return
    }

    // update pinia to communicate with calendar modal
    // only do this once... button is on page twice
    if (!this.fetchDisabled) {
      this.setScheduleDate(this.scheduleDate)
    }

    this.fetchSchedule()

    // if fetch is disabled, set loading to force loading state
    // as a second button is fetching... wait for data to load before disabling
    if (this.fetchDisabled) {
      this.loading = true
    }
  },

  methods: {
    ...mapActions(useCalendarStore, ['addScheduledItems', 'setScheduleDate']),
    ...mapActions(useModalsStore, ['showLoginModal']),

    /**
     * Return any scheduled instances for schedulable type / id for "scheduleDate"
     */
    async fetchSchedule() {
      // no double fetch
      if (this.fetchDisabled || this.loading) {
        return
      }

      this.loading = true

      try {
        // we have already toggled isCompleteData, so api url is backwards
        const res = await fbApi.get(`/calendar/schedule/${this.scheduleDate}/check`, {
          searchParams: objToSearchParams({
            schedulableType: this.entityType,
            schedulableId: this.entityId,
          }),
        })

        if (!res || !res.body || !res.body.data) {
          throw Error(`Unable to fetch user schedule`)
        }

        // pinia uses on multi-complete-button
        // and calendar day view
        this.addScheduledItems(res.body.data.userScheduleItems)
      } catch (err) {
        // user will see the error modal prompting them to refresh
        // complete button will be hidden
        this.error = `Error fetching schedule`
      } finally {
        this.loading = false
      }
    },

    // similar modal
    handleNoAuthComplete() {
      this.showLoginModal()
    },
  },
})
</script>
